import { QuoteEngine } from './components/QuoteEngine';
import { AuthModal } from './components/AuthModal';
import { FormLoader } from './components/FormLoader';
import { AddressBookModal } from './components/AddressBookModal';

class LogisticsSDK {
  constructor(config) {
    this.clientKey = config?.client_key || null;
    this.tenantId = config?.tenantId || config?.tenant_id || config?.tenantCode || null;
    this.isAuthenticated = typeof window !== 'undefined' ? !!localStorage.getItem('session_token') : false; // Check session token on init
    this.widgets = new Set();
    this.authModalInstance = null; // Decoupled auth modal instance
    this.addressBookModalInstance = null; // Decoupled address book modal instance
    this.auth = {
      isLoggedIn: () => typeof window !== 'undefined' ? !!localStorage.getItem('session_token') : false
    };
  }

  // Programmatic method to open auth modal
  openAuthModal(callback) {
    this.triggerAuth(callback);
  }

  // Programmatic builder for Quote Widget
  initQuoteWidget(config) {
    if (!config || !config.div_id) {
      console.error('[LogisticsSDK] Missing required parameter "div_id" in initQuoteWidget.');
      return;
    }

    const element = document.getElementById(config.div_id);
    if (!element) {
      console.error(`[LogisticsSDK] Target container #${config.div_id} not found on page.`);
      return;
    }

    if (element.dataset.logisticsInitialized) {
      console.warn(`[LogisticsSDK] Widget inside #${config.div_id} is already initialized.`);
      return;
    }

    element.dataset.logisticsInitialized = 'true';
    const widget = new QuoteEngine(this, element);
    this.widgets.add(widget);

    console.log(`[LogisticsSDK] Quote Widget initialized successfully on #${config.div_id} using key: ${this.clientKey}`);
    return widget;
  }

  // Programmatic builder for Decoupled Auth Modal
  initAuthModal(config) {
    if (!config || !config.div_id) {
      console.error('[LogisticsSDK] Missing required parameter "div_id" in initAuthModal.');
      return;
    }

    const element = document.getElementById(config.div_id);
    if (!element) {
      console.error(`[LogisticsSDK] Target container #${config.div_id} not found on page.`);
      return;
    }

    if (element.dataset.logisticsInitialized) {
      console.warn(`[LogisticsSDK] Auth modal inside #${config.div_id} is already initialized.`);
      return;
    }

    element.dataset.logisticsInitialized = 'true';

    const modal = new AuthModal(this, element);
    console.log(`[LogisticsSDK] Decoupled Auth Modal initialized successfully on #${config.div_id}`);
    return modal;
  }

  // Programmatic builder for Decoupled Address Book Modal
  initAddressBookModal(config) {
    if (!config || !config.div_id) {
      console.error('[LogisticsSDK] Missing required parameter "div_id" in initAddressBookModal.');
      return;
    }

    const element = document.getElementById(config.div_id);
    if (!element) {
      console.error(`[LogisticsSDK] Target container #${config.div_id} not found on page.`);
      return;
    }

    if (element.dataset.logisticsInitializedAddress) {
      console.warn(`[LogisticsSDK] Address book modal inside #${config.div_id} is already initialized.`);
      return;
    }

    element.dataset.logisticsInitializedAddress = 'true';

    const modal = new AddressBookModal(this, element);
    console.log(`[LogisticsSDK] Decoupled Address Book Modal initialized successfully on #${config.div_id}`);
    return modal;
  }

  // Programmatic builder for Form Loader
  initFormLoader(config) {
    if (!config || !config.div_id) {
      console.error('[LogisticsSDK] Missing required parameter "div_id" in initFormLoader.');
      return;
    }

    const element = document.getElementById(config.div_id);
    if (!element) {
      console.error(`[LogisticsSDK] Target container #${config.div_id} not found on page.`);
      return;
    }

    if (element.dataset.logisticsInitialized) {
      console.warn(`[LogisticsSDK] Form Loader inside #${config.div_id} is already initialized.`);
      return;
    }

    element.dataset.logisticsInitialized = 'true';
    const loader = new FormLoader(this, element, config);
    console.log(`[LogisticsSDK] Form Loader initialized successfully on #${config.div_id} with formId: ${config.formId}`);
    return loader;
  }

  // Alias to ensure compatibility with standard typos
  initFromLoader(config) {
    return this.initFormLoader(config);
  }

  // API logout call and session cleanup
  async logout() {
    const token = localStorage.getItem('session_token');
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      await fetch('https://dev-api.shipdeez.com/api/v1/Auth/logout', {
        method: 'POST',
        headers,
        body: JSON.stringify({})
      });
    } catch (err) {
      console.error('[LogisticsSDK] API Logout request failed:', err);
    } finally {
      // Remove all local storage items
      localStorage.removeItem('storage_token');
      localStorage.removeItem('session_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('auth_code');
      localStorage.removeItem('user_info');
      localStorage.removeItem('user_permissions');
      localStorage.removeItem('selected_tenant');

      // Remove cookies
      document.cookie = "session_token=; path=/; max-age=0; SameSite=Lax; Secure";
      document.cookie = "selected_tenant=; path=/; max-age=0; SameSite=Lax; Secure";

      this.setAuth(false);
      console.log('[LogisticsSDK] Client session cleared successfully.');
    }
  }

  // Fetch address book items
  async getAddressBook(payload) {
    const token = typeof window !== 'undefined' ? localStorage.getItem('session_token') : null;
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch('https://dev-api.shipdeez.com/api/v1/Address/get-address-book', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });
    return response.json();
  }

  // Register the auth modal instance for quote widget callbacks
  registerAuthModal(instance) {
    this.authModalInstance = instance;
  }

  // Register the address book modal instance
  registerAddressBookModal(instance) {
    this.addressBookModalInstance = instance;
  }

  // Fire address book popup callback
  triggerAddressBook(rowId, rowType, callback) {
    if (this.addressBookModalInstance) {
      console.log('[LogisticsSDK] Triggering Address Book Modal for row:', rowId, rowType);
      this.addressBookModalInstance.show(rowId, rowType, callback);
    } else {
      console.warn('[LogisticsSDK] No separate address book modal registered on DOM.');
    }
  }

  // Fire auth popup callback
  triggerAuth(callback) {
    if (this.authModalInstance) {
      console.log('[LogisticsSDK] Triggering auth popup via separate Auth Modal component.');
      this.authModalInstance.show(callback);
    } else {
      console.warn('[LogisticsSDK] No separate auth modal registered on DOM. Using credentials prompt fallback.');
      const email = prompt("Enter email account to continue booking:");
      if (email && email.includes('@')) {
        this.setAuth(true);
        callback(true);
      } else {
        alert("Valid authentication required.");
        callback(false);
      }
    }
  }

  // Global auth state toggle
  setAuth(status) {
    this.isAuthenticated = status;
    console.log(`[LogisticsSDK] Authentication status changed: ${this.isAuthenticated}`);
  }

  // Helper to load external scripts asynchronously
  lazyLoadScript(src) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        if (window.Stripe || src.indexOf('stripe') === -1) {
          resolve();
        } else {
          let interval = setInterval(() => {
            if (window.Stripe) {
              clearInterval(interval);
              resolve();
            }
          }, 50);
          setTimeout(() => {
            clearInterval(interval);
            resolve();
          }, 3000);
        }
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load: ${src}`));
      document.head.appendChild(script);
    });
  }
}

// Export the Constructor Class directly to Global namespace
if (typeof window !== 'undefined') {
  window.LogisticsSDK = LogisticsSDK;
}
